import React, { useEffect, useMemo, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../utils/methods';
import ConfirmModal from '../common/modal/ConfirmModal';
import { createGroupBatchReserve, previewGroupBatchReserve } from '../../api/lesson';
import {
  EditModalContainer,
  EditHeader,
  EditHeaderTitle,
  EditHeaderClose,
  EditBody,
  EditFooter,
  SummaryText,
  SummaryStrong,
  SummaryMuted,
  FooterActions,
  CancelButton,
  ApplyButton,
} from '../member/modal/MemberRecipientPickerModalStyled';
import { ReserveMemberPicker } from './groupLesson/groupLessonTemplate/ReserveMemberSelectModal';
import ReserveMemberResultList from './groupLesson/groupLessonTemplate/ReserveMemberResultList';

const STEPS = ['회원 선택', '확인·예약'];
const disabledSx = { opacity: 0.5, pointerEvents: 'none' };
const panelSx = {
  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', backgroundColor: '#fff',
  border: '1px solid #d6dbdf', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(28,38,46,0.08)',
};
const statSx = { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '96px', px: '14px', py: '8px', borderRadius: '8px', backgroundColor: '#f7f8fa' };
const statLabelSx = { fontSize: '11.5px', color: '#8c8c8c', fontWeight: 600 };
const statValueSx = { fontSize: '18px', fontWeight: 700, color: '#262626', lineHeight: 1.3 };

/**
 * 참석관리(수업 상세)에서 이 수업 한 회차에 여러 회원을 일괄 예약하는 서브모달.
 *
 * 예전엔 회원 그룹 하나만 골라 회원별로 수강권을 따로 조회하고 1명씩 순차 예약했다(그룹 밖 회원 불가, 대기 등록 포함).
 * 이제 그룹수업 일괄예약과 같은 피커(검색·수강권 칩·그룹 불러오기·담기)와 일괄예약 API(`courseClassIds=[이 회차]`)를 쓴다.
 * 판정은 회원 단위 전부-아니면-전무(회차가 하나라 곧 회원별 가능/불가), 이미 예약·대기 중인 회원은 건너뛴다.
 * 정원 내 예약만 — 대기 등록은 제거(사용자 확정 2026-09-22). 앱 알림 없음.
 */
export default function GroupReserveModal({
  open,
  onClose,
  courseClassTimeId,
  lesson, // {tickets:[{ticketId, ticketName}], membersMaxCount}
  currentUsers = [], // 이 회차의 기존 예약/출석/대기 목록 — 남은 정원 계산용
  onCompleted, // 1건이라도 예약되면 부모 새로고침
}) {
  const toast = useToast();
  const { user } = useSelector(state => state.user);
  const branchId = user?.branch?.branchId;

  const [step, setStep] = useState(0);
  const [members, setMembers] = useState([]);
  const [memberTicketIds, setMemberTicketIds] = useState({}); // 회원별로 쓸 상품(수강권·일일권)
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false); // 예약 실행 전 최종 확인
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setMembers([]);
    setMemberTicketIds({});
    setSubmitConfirmOpen(false);
    setPreview(null);
  }, [open]);

  // 칩 = 이 수업에 매핑된 수강권. 일일권은 수업 정보에 없어 칩으로는 안 나오지만 후보·선택지에는 포함된다.
  const ticketOptions = useMemo(
    () => (lesson?.tickets || []).map(t => ({
      id: t.ticketId ?? t, name: t.ticketName ?? String(t), ticketType: 'COURSE',
    })),
    [lesson],
  );
  // 남은 정원 = 정원 - (예약중 + 출석). 대기·취소·결석은 자리를 차지하지 않는다. 정원 null 은 무제한, 0 은 0자리(백엔드 판정과 동일).
  const occupied = currentUsers.filter(u => u.status === 'RESERVED' || u.status === 'ATTENDED').length;
  const unlimited = lesson?.membersMaxCount == null;
  const remaining = unlimited ? null : Math.max(0, lesson.membersMaxCount - occupied);
  const remainingLabel = unlimited ? '무제한' : `${remaining}명`;

  // 회원별로 고른 상품을 종류별 맵으로 나눈다(백엔드는 수강권·일일권을 따로 받는다).
  const memberPickPayload = () => {
    const course = {};
    const trial = {};
    Object.entries(memberTicketIds).forEach(([memberId, pick]) => {
      if (!pick) return;
      if (pick.ticketType === 'TRIAL') trial[memberId] = pick.productId;
      else course[memberId] = pick.productId;
    });
    return { memberCourseTicketIds: course, memberTrialTicketIds: trial };
  };

  const runPreview = async (memberList = members) => {
    setPreviewing(true);
    try {
      const data = await previewGroupBatchReserve({
        courseClassIds: [courseClassTimeId], memberIds: memberList.map(m => m.memberId), ...memberPickPayload(),
      }, user.token);
      setPreview(data);
    } catch (error) {
      toast.error(errorMessages(error));
      setStep(0);
    } finally {
      setPreviewing(false);
    }
  };

  // 확인 단계에서 회원을 빼면 남은 회원으로 다시 판정한다. 다 빼면 회원 선택으로 돌아간다.
  const removeMember = memberId => {
    const next = members.filter(m => m.memberId !== memberId);
    setMembers(next);
    setPreview(null);
    if (next.length === 0) { setStep(0); return; }
    runPreview(next);
  };

  const goNext = () => {
    if (members.length === 0) { toast.error('회원을 선택해 주세요'); return; }
    setPreview(null);
    setStep(1);
    runPreview();
  };

  const excludedCount = preview ? preview.members.filter(m => m.failures.length > 0).length : 0;
  const reservableCount = preview ? preview.members.filter(m => m.failures.length === 0 && m.reservedCount > 0).length : 0;
  const busy = previewing || saving;
  const close = () => { if (!busy) onClose(); };

  const submit = async () => {
    setSubmitConfirmOpen(false);
    setSaving(true);
    try {
      const res = await createGroupBatchReserve({
        courseClassIds: [courseClassTimeId], memberIds: members.map(m => m.memberId), ...memberPickPayload(),
      }, user.token);
      const excluded = (res?.members || []).filter(m => (m.failures || []).length > 0).length;
      if (excluded > 0) toast.warning(`회원 ${res.reservedMemberCount}명 예약 완료. 예약 불가 ${excluded}명은 제외되었습니다`);
      else toast.success(`회원 ${res?.reservedMemberCount ?? 0}명을 예약했습니다`);
      if ((res?.reservedMemberCount ?? 0) > 0) onCompleted?.();
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSaving(false);
    }
  };

  const renderStepper = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ml: '16px' }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', opacity: i === step ? 1 : 0.5 }}>
            <Box sx={{
              width: '18px', height: '18px', borderRadius: '50%', fontSize: '11px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: i <= step ? '#4AB3BC' : 'rgba(255,255,255,0.25)', color: '#fff',
            }}>{i + 1}</Box>
            <Typography sx={{ fontSize: '12.5px', fontWeight: i === step ? 700 : 500, color: '#fff' }}>{label}</Typography>
          </Box>
          {i < STEPS.length - 1 && <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>›</Typography>}
        </React.Fragment>
      ))}
    </Box>
  );

  return (
    <>
    <ConfirmModal
      open={submitConfirmOpen}
      onClose={() => setSubmitConfirmOpen(false)}
      onConfirm={submit}
      message="선택한 회원을 이 수업에 예약할까요?"
      highlightText="__none__"
      detail={
        // ConfirmModal 제목의 marginBottom 24px 를 상쇄해 아래 요소를 붙인다.
        <Box sx={{ mt: '-14px', mb: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '18px', py: '10px', borderRadius: '8px', backgroundColor: '#f7f8fa' }}>
            <Typography sx={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 600 }}>회원</Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0F7F85' }}>{reservableCount}명</Typography>
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#737373', textAlign: 'center', lineHeight: 1.6, whiteSpace: 'nowrap' }}>
            {excludedCount > 0 ? <>예약 불가 {excludedCount}명은 제외하고 진행합니다.<br /></> : null}
            이미 예약된 회원은 건너뜁니다. 앱 알림은 보내지 않습니다.
          </Typography>
        </Box>
      }
      cancelText="취소"
      confirmText="예약"
      confirmColor="#4AB3BC"
    />
    <Modal open={open} onClose={close}>
      <EditModalContainer sx={{ height: '680px' }}>
        <EditHeader>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditHeaderTitle>이 수업에 일괄 예약</EditHeaderTitle>
            {renderStepper()}
          </Box>
          <EditHeaderClose onClick={close}>✕</EditHeaderClose>
        </EditHeader>

        {step === 0 && (
          <ReserveMemberPicker
            open={open && step === 0}
            token={user.token}
            branchId={branchId}
            courseClassIds={[courseClassTimeId]}
            ticketOptions={ticketOptions}
            maxCount={remaining}
            limitLabel="남은 정원"
            picked={members}
            onPickedChange={next => { setMembers(next); setPreview(null); }}
            memberTicketIds={memberTicketIds}
            onMemberTicketChange={(memberId, productId, ticketType) => {
              setMemberTicketIds(prev => ({ ...prev, [memberId]: { productId, ticketType } }));
              setPreview(null);
            }}
          />
        )}
        {step === 1 && (
          <EditBody sx={{ flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ ...panelSx, flex: 'none', p: '12px 16px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
              <Box sx={statSx}><Typography sx={statLabelSx}>회원</Typography><Typography sx={statValueSx}>{members.length}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></Typography></Box>
              <Box sx={{ ...statSx, backgroundColor: '#E4F5F6' }}><Typography sx={{ ...statLabelSx, color: '#0F7F85' }}>예약가능</Typography><Typography sx={{ ...statValueSx, color: '#0F7F85' }}>{preview ? reservableCount : '-'}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></Typography></Box>
              <Box sx={{ ...statSx, backgroundColor: excludedCount > 0 ? '#FDF0EF' : '#f7f8fa' }}><Typography sx={{ ...statLabelSx, color: excludedCount > 0 ? '#C2403D' : '#8c8c8c' }}>제외</Typography><Typography sx={{ ...statValueSx, color: excludedCount > 0 ? '#C2403D' : '#262626' }}>{preview ? excludedCount : '-'}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></Typography></Box>
              <Box sx={statSx}><Typography sx={statLabelSx}>남은 정원</Typography><Typography sx={statValueSx}>{unlimited ? '무제한' : <>{remaining}<span style={{ fontSize: 12, marginLeft: 2 }}>명</span></>}</Typography></Box>
              {preview && excludedCount > 0 && (
                <Typography sx={{ fontSize: '12px', color: '#C2403D', ml: '8px' }}>예약 불가 회원은 제외됩니다. 이미 예약된 회원은 건너뜁니다.</Typography>
              )}
            </Box>
            <Box sx={panelSx}>
              <Box sx={{ px: '16px', py: '8px', backgroundColor: '#f7f8fa', borderBottom: '1px solid #eceff1', fontSize: '12px', fontWeight: 700, color: '#8c8c8c' }}>
                회원별 판정
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <ReserveMemberResultList members={preview?.members} loading={previewing || !preview} onRemove={removeMember} />
              </Box>
            </Box>
          </EditBody>
        )}

        <EditFooter>
          <SummaryText>
            {step === 0 && (
              <>
                <SummaryStrong>{members.length}명</SummaryStrong>
                <SummaryMuted>/ 남은 정원 {remainingLabel}</SummaryMuted>
              </>
            )}
            {step === 1 && preview && (
              <>
                <SummaryStrong>예약 {reservableCount}명</SummaryStrong>
                {excludedCount > 0 && <SummaryMuted>· 제외 {excludedCount}명</SummaryMuted>}
              </>
            )}
          </SummaryText>
          <FooterActions>
            <CancelButton onClick={step === 0 ? close : () => setStep(0)} sx={busy ? disabledSx : {}}>{step === 0 ? '취소' : '이전'}</CancelButton>
            {step === 0 ? (
              <ApplyButton onClick={goNext} sx={members.length > 0 ? {} : disabledSx}>다음</ApplyButton>
            ) : (
              <ApplyButton onClick={() => setSubmitConfirmOpen(true)} sx={!preview || busy || reservableCount === 0 ? disabledSx : {}}>
                {saving ? '예약 중...' : excludedCount > 0 ? '예약 불가 제외 후 예약' : '일괄 예약'}
              </ApplyButton>
            )}
          </FooterActions>
        </EditFooter>
      </EditModalContainer>
    </Modal>
    </>
  );
}
